import { Fragment, Slice, Schema, Node as PMNode } from '@tiptap/pm/model';
import { EditorView } from '@tiptap/pm/view';
import { EditorState } from '@tiptap/pm/state';

import config from '../../../../config';
import { staticFunctions } from '@shared/contexts';
import { TImageNodeAttrs } from '../../types';
import { ELEMENT_ROW_NODE_NAME } from '../nodes/elementRow/constants';
import { ETiptapAlignmentSelectorKey } from '../../types/tiptap.types';

/** Hard limit with uniform UX */
export const checkImageSizeLimit = (file: File): boolean => {
  if (file.size > config.MAX_FILE_SIZES_MB.DOCUMENT_IMAGE * 1024 * 1024) {
    staticFunctions.show('error', `Image size exceeds our limit of ${config.MAX_FILE_SIZES_MB.DOCUMENT_IMAGE}MB!`);
    return false;
  }
  return true;
};

export const widthForCount = (count: number): string => (count <= 1 ? 'auto' : count === 2 ? '65%' : '50%');

/** Build image nodes; optionally pre-assign width to improve row layout */
export const buildImageNodes = (schema: Schema, files: File[], width?: string): PMNode[] => {
  const nodes: PMNode[] = [];
  for (const f of files) {
    const attrs: TImageNodeAttrs = { file: f };
    if (width) attrs.width = width;
    nodes.push(schema.nodes.image.create(attrs));
  }
  return nodes;
};

export const wrapInElementRow = (schema: Schema, children: PMNode[], align: string): Fragment => {
  const rowType = schema.nodes[ELEMENT_ROW_NODE_NAME];
  if (!rowType) return Fragment.fromArray(children); // schema without row → insert children as-is
  const row = rowType.create({ textAlign: align }, Fragment.fromArray(children));
  return Fragment.fromArray([row]);
};

export const isInsideRowAtPos = (state: EditorState, pos?: number): boolean => {
  const rowType = state.schema.nodes[ELEMENT_ROW_NODE_NAME];
  if (!rowType) return false;
  const $pos = pos != null ? state.doc.resolve(pos) : state.selection.$from;
  for (let d = $pos.depth; d >= 0; d--) if ($pos.node(d).type === rowType) return true;
  return false;
};

type InsertImagesOpts = {
  view: EditorView;
  files: File[]; // raw files (we’ll size-filter below)
  insertPos?: number; // if omitted, replaceSelection
  eventMeta: 'paste' | 'drop';
};

/**
 * One canonical insertion path for both paste & drop.
 * - Filters size
 * - Decides row-wrapping + widths
 * - Emits a single history event
 */
export const insertImages = ({ view, files, insertPos, eventMeta }: InsertImagesOpts): boolean => {
  const { schema } = view.state;
  const valid = files.filter(checkImageSizeLimit);
  if (!valid.length) return true; // handled event but nothing to insert

  const insideRow = isInsideRowAtPos(view.state, insertPos);
  const count = valid.length;

  const frag = (() => {
    if (insideRow || count === 1) {
      // Already in row OR single image → no wrapper, no forced widths
      return Fragment.fromArray(buildImageNodes(schema, valid));
    }
    // Multiple images outside a row → wrap & assign width per count
    const w = widthForCount(count);
    return wrapInElementRow(schema, buildImageNodes(schema, valid, w), ETiptapAlignmentSelectorKey.CENTER);
  })();

  const tr = (
    insertPos == null ? view.state.tr.replaceSelection(new Slice(frag, 0, 0)) : view.state.tr.insert(insertPos, frag)
  )
    .setMeta('uiEvent', eventMeta)
    .setMeta(eventMeta, true)
    .setMeta('addToHistory', true)
    .scrollIntoView();

  view.dispatch(tr);
  return true;
};
